import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { Datastore } from '@freelancer/datastore';
import {
  DiscoverCollection,
  DiscoverCollectionsCollection,
} from '@freelancer/datastore/collections';
import { ModalService } from '@freelancer/ui';
import { ModalSize } from '@freelancer/ui/modal';
import { MoreOptionsSize } from '@freelancer/ui/more-options';
import { QueryParamsHandling } from '@freelancer/ui/tabs';
import { DiscoverCollectionTypeApi } from 'api-typings/users/users';
import { ConfirmationResult } from './user-list-confirmation-modal/user-list-confirmation-modal.types';

@Component({
  selector: 'app-user-list-more-actions',
  template: `
    <fl-more-options
      *ngIf="showMoreOptions"
      flTrackingLabel="MoreOptions"
      flTrackingReferenceType="collection_id"
      [flTrackingReferenceId]="userList.id"
      [size]="MoreOptionsSize.XSMALL"
    >
      <fl-more-options-item
        i18n="Edit button text"
        flTrackingLabel="EditCollection"
        flTrackingReferenceType="collection_id"
        [flTrackingReferenceId]="userList.id"
        (click)="openEditModal()"
      >
        Edit
      </fl-more-options-item>
      <fl-more-options-item
        i18n="Delete button text"
        flTrackingLabel="DeleteCollection"
        flTrackingReferenceType="collection_id"
        [flTrackingReferenceId]="userList.id"
        (click)="openConfirmationModal()"
      >
        Delete
      </fl-more-options-item>
    </fl-more-options>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserListMoreActionsComponent implements OnChanges {
  MoreOptionsSize = MoreOptionsSize;

  @Input() userList: DiscoverCollection;

  showMoreOptions = true;

  constructor(
    private datastore: Datastore,
    private modalService: ModalService,
    private router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ('userList' in changes && this.userList) {
      this.showMoreOptions =
        this.userList.type === DiscoverCollectionTypeApi.LISTS_CUSTOM;
    }
  }

  openEditModal(): void {
    this.modalService.open('UserListSaveModal', {
      inputs: {
        userList: this.userList,
      },
      edgeToEdge: true,
      size: ModalSize.SMALL,
      mobileFullscreen: true,
    });
  }

  openConfirmationModal(): void {
    const listId = this.userList.id;
    this.modalService
      .open('UserListConfirmationModal', {
        size: ModalSize.XSMALL,
        mobileFullscreen: true,
      })
      .afterClosed()
      .toPromise()
      .then(result => {
        if (result === ConfirmationResult.ACCEPT) {
          this.datastore
            .collection<DiscoverCollectionsCollection>('discoverCollections')
            .remove(listId)
            .then(res => {
              if (
                res.status === 'success' &&
                /**
                 * If the list being viewed has been deleted via the datastore and the current url
                 * contains the deleted list id, we should navigate back to the root lists url
                 * e.g if the deleted list id is 12345 and the list name was example, the url of the page
                 * would be '/lists/example_12345' if the list is currently being viewed but this would be
                 * an invalid route after deletion, so we would navigate back to '/lists'
                 */
                this.router.url.includes(listId.toString())
              ) {
                this.router.navigate(['/lists'], {
                  queryParamsHandling: QueryParamsHandling.PRESERVE,
                  // Replace current state in history
                  replaceUrl: true,
                });
              }
            });
        }
      });
  }
}
